import { useEffect, useState } from "react";

import copyToClipboardWithExecCommand from "copy-to-clipboard";
import uuid4 from "uuid/v4";

import { inCalendar, inVideocall } from "../browserExtension";
import store from "../store";
import { isTeams, isZoom } from "./platform";

/**
 *	@deprecated
 */
export const uniqueId = (length = 8): string => {
	let id = "";
	while (id.length < length) {
		id += Math.random().toString(36).substr(2);
	}
	return id.substr(0, length);
};

export async function makeServiceWorkerCall(action: string, data: object, timeout?: number) {
	if (!window.navigator.serviceWorker?.controller?.postMessage) {
		throw new Error("Service worker not available.");
	}

	const requestRef = uuid4();

	const message = {
		action: action,
		data: data,
		ref: requestRef,
	};

	const callResponsePromise = new Promise((resolve, reject) => {
		let timeoutRef: number | null = null;

		if (timeout) {
			timeoutRef = window.setTimeout(() => {
				reject(new Error("Service worker call timed out."));
				window.navigator.serviceWorker?.removeEventListener("message", messageListener);
			}, timeout);
		}

		const messageListener = (event: MessageEvent) => {
			if (typeof event.data !== "object" || !event.data) {
				return;
			}

			const { action: responseAction, data: responseData, ref: responseRef } = event.data;

			if (requestRef === responseRef && action === responseAction && typeof responseData === "object") {
				window.navigator.serviceWorker?.removeEventListener("message", messageListener);
				if (timeoutRef) {
					window.clearTimeout(timeoutRef);
				}
				resolve(responseData);
			}
		};

		window.navigator.serviceWorker?.addEventListener("message", messageListener);
	});

	window.navigator.serviceWorker?.controller?.postMessage(message);
	await callResponsePromise;
}

export const libStore = store.namespace("lib");
export const LAST_LOCATION_KEY = "lastAppLocation";

export function download(filename: string, blob: Blob) {
	const a = window.document.createElement("a");
	a.href = window.URL.createObjectURL(blob);
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
}

export function contactSupport(message = "") {
	if (window.Intercom) {
		return window.Intercom("showNewMessage", message);
	}

	const mailUrl = `mailto:support@fellow.co?body=${message ? encodeURIComponent(message) : ""}`;
	window.open(mailUrl, "_blank", "noopener noreferrer");
}

/* Replace this with a list of article IDs pulled from Intercom to validate */
export type ArticleID = string;

export function showHelpCenter(articleId?: string) {
	// The Intercom launcher has a bug with `showArticle` where it gets in a bad state
	// Keep this commented out until it's fixed

	// if (window.Intercom) {
	// 	if (articleId) {
	// 		window.Intercom("showArticle", articleId);
	//
	// 		// Shrink the size of the Intercom help window
	// 		const intervalId = window.setInterval(() => {
	// 			const largeFrame = document.querySelector<HTMLDivElement>("div.intercom-messenger-frame.intercom-4n9m1z");
	// 			if (!largeFrame) return;
	//
	// 			const frame = document.querySelector<HTMLIFrameElement>("[data-intercom-frame]");
	// 			if (!frame) return;
	//
	// 			const toggle = frame.contentDocument?.querySelector<HTMLButtonElement>("[data-testid=toggle-expansion]");
	// 			toggle?.click();
	// 			clearInterval(intervalId);
	// 		}, 200);
	//
	// 		setTimeout(() => {
	// 			clearInterval(intervalId);
	// 		}, 10000);
	//
	// 		return;
	// 	}
	//
	// 	// Hidden API method that opens Intercom on a specific tab
	// 	return window.Intercom("showSpace", "help");
	// }

	if (articleId) {
		return window.open(`https://help.fellow.app/en/articles/${articleId}`, isTeams ? "_self" : "_blank", "noopener");
	}

	return window.open("https://help.fellow.app", isTeams ? "_self" : "_blank", "noopener");
}

/**
 * Copy some text to the user's clipboard.
 *
 * When copying something async, call this function synchronously and pass it
 * the promise instead of awaiting it outside of this function.
 */
export async function copyToClipboard(text: string | Promise<string>): Promise<void> {
	// In MS Teams (and browser extensions) the fancier clipboard APIs we use below don't work due to some
	// permission error (maybe due to how Fellow is running within Teams).
	// Instead, we'll just fall back to the old-school way, where the text is
	// written to a DOM node, selected, and then document.execCommand("copy") is
	// used to copy to the clipboard. We can't use this all the time due to the
	// issues in the comment below.
	if (isTeams || inCalendar || inVideocall || isZoom) {
		if (!copyToClipboardWithExecCommand(await text)) {
			throw new Error("Failed to copy to clipboard");
		}
		return;
	}

	if (!(text instanceof Promise)) {
		return await navigator.clipboard.writeText(text);
	}

	// To write to the clipboard requires a "transient user activation" [1]. This
	// means that you can only do so after some user interactions and before some
	// amount of time has passed after that interaction. This makes it difficult
	// to copy the result of an API request to the clipboard.
	//
	// In Chrome and Safari (and Firefox if a feature flag is enabled [2]), we
	// can use the async clipboard API, where we pass a Promise<string> to a
	// ClipboardItem. Otherwise, we just await the text and hope that the timeout
	// has yet to expire when we write it to the clipboard.
	//
	// [1]: https://developer.mozilla.org/en-US/docs/Glossary/Transient_activation
	// [2]: https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem/ClipboardItem
	if (typeof ClipboardItem !== "undefined") {
		// MDN says that we can pass a promise of either a string or a blob as the
		// data in the ClipboardItem, but Chrome expects a blob.
		const blobPromise = text.then(content => new Blob([content], { type: "text/plain" }));
		return await navigator.clipboard.write([new ClipboardItem({ "text/plain": blobPromise })]);
	} else {
		return await navigator.clipboard.writeText(await text);
	}
}

export function useNewest<T>(valueA: T, valueB: T): T {
	const [newest, setNewest] = useState(valueA);

	useEffect(() => {
		setNewest(valueB);
	}, [valueB]);

	useEffect(() => {
		setNewest(valueA);
	}, [valueA]);

	return newest;
}
